/**
 * rtc客户端
 */
import VERTC, {
  StreamIndex,
  IRTCEngine,
  RoomProfileType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  MediaType,
  LocalStreamStats,
  RemoteStreamStats,
  StreamRemoveReason,
  AudioProfileType,
  DeviceInfo,
  AutoPlayFailedEvent,
  PlayerEvent,
} from '@volcengine/rtc';
import RTCAIAnsExtension from '@volcengine/rtc/extension-ainr';
import openAPIs from '@/app/api';
import aigcConfig from '@/config';
import Utils from '@/utils/utils';

export interface IEventListener {
  handleUserJoin: (e: onUserJoinedEvent) => void;
  handleUserLeave: (e: onUserLeaveEvent) => void;
  handleUserPublishStream: (e: { userId: string; mediaType: MediaType }) => void;
  handleUserUnpublishStream: (e: {
    userId: string;
    mediaType: MediaType;
    reason: StreamRemoveReason;
  }) => void;
  handleRemoteStreamStats: (e: RemoteStreamStats) => void;
  handleLocalStreamStats: (e: LocalStreamStats) => void;
  handleAudioDeviceStateChanged: (e: DeviceInfo) => void;
  handleAutoPlayFail: (e: AutoPlayFailedEvent) => void;
  handlePlayerEvent: (e: PlayerEvent) => void;
  handleUserStartAudioCapture: (e: { userId: string }) => void;
  handleUserStopAudioCapture: (e: { userId: string }) => void;
  handleRoomMessageReceived: (e: any) => void;
  handleRoomBinaryMessageReceived: (e: any) => void;
}

interface EngineOptions {
  appId: string;
  uid: string;
  roomId: string;
  login_token: string | null;
}

export interface BasicBody {
  room_id: string;
  user_id: string;
  login_token: string | null;
}


/**
 * @brief RTC Core Client
 * @notes Refer to official website documentation to get more information about the API.
 */
export class RTCClient {
  engine!: IRTCEngine;

  config!: EngineOptions;

  rtsBody!: BasicBody;

  private _audioCaptureDevice?: string;

  audioBotEnabled: boolean = Utils.getAudioBotEnabled();

  audioBotStartTime = 0;

  /**
   * 创建引擎
   * @param props
   */
  createEngine = async (props: EngineOptions) => {
    this.config = props;
    this.rtsBody = {
      room_id: props.roomId,
      user_id: props.uid,
      login_token: props.login_token,
    };

    this.engine = VERTC.createEngine(this.config.appId);
    try {
      const AIAnsExtension = new RTCAIAnsExtension();
      await this.engine.registerExtension(AIAnsExtension);
      // TODO 关闭了ai降噪
      //AIAnsExtension.enable();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * 增加事件监听
   * @param handleUserJoin
   * @param handleUserLeave
   * @param handleUserPublishStream
   * @param handleUserUnpublishStream
   * @param handleRemoteStreamStats
   * @param handleLocalStreamStats
   * @param handleAudioDeviceStateChanged
   * @param handleAutoPlayFail
   * @param handlePlayerEvent
   * @param handleUserStartAudioCapture
   * @param handleUserStopAudioCapture
   * @param handleRoomMessageReceived
   * @param handleRoomBinaryMessageReceived
   */
  addEventListeners = ({
                         handleUserJoin,
                         handleUserLeave,
                         handleUserPublishStream,
                         handleUserUnpublishStream,
                         handleRemoteStreamStats,
                         handleLocalStreamStats,
                         handleAudioDeviceStateChanged,
                         handleAutoPlayFail,
                         handlePlayerEvent,
                         handleUserStartAudioCapture,
                         handleUserStopAudioCapture,
                         handleRoomMessageReceived,
                         handleRoomBinaryMessageReceived,
                       }: IEventListener) => {
    this.engine.on(VERTC.events.onUserJoined, handleUserJoin);
    this.engine.on(VERTC.events.onUserLeave, handleUserLeave);
    this.engine.on(VERTC.events.onUserPublishStream, handleUserPublishStream);
    this.engine.on(VERTC.events.onUserUnpublishStream, handleUserUnpublishStream);
    this.engine.on(VERTC.events.onRemoteStreamStats, handleRemoteStreamStats);
    this.engine.on(VERTC.events.onLocalStreamStats, handleLocalStreamStats);
    this.engine.on(VERTC.events.onAudioDeviceStateChanged, handleAudioDeviceStateChanged);

    this.engine.on(VERTC.events.onAutoplayFailed, handleAutoPlayFail);
    this.engine.on(VERTC.events.onPlayerEvent, handlePlayerEvent);
    this.engine.on(VERTC.events.onUserStartAudioCapture, handleUserStartAudioCapture);
    this.engine.on(VERTC.events.onUserStopAudioCapture, handleUserStopAudioCapture);
    this.engine.on(VERTC.events.onRoomMessageReceived, handleRoomMessageReceived);
    this.engine.on(VERTC.events.onRoomBinaryMessageReceived, handleRoomBinaryMessageReceived);
  };

  /**
   * 加入房间
   * @param token
   * @param username
   */
  joinRoom = (token: string | null, username: string): Promise<void> => {
    this.engine.enableAudioPropertiesReport({ interval: 2000 });
    return this.engine.joinRoom(
      token,
      `${this.config.roomId!}`,
      {
        userId: this.config.uid!,
        extraInfo: JSON.stringify({
          user_name: username,
          user_id: this.config.uid,
        }),
      },
      {
        isAutoPublish: true,
        isAutoSubscribeAudio: true,
      },
    );
  };

  /**
   * 离开房间
   */
  leaveRoom = () => {
    this.stopAudioBot(this.rtsBody.room_id, this.rtsBody.user_id);
    this.engine.leaveRoom();
    VERTC.destroyEngine(this.engine);
    this._audioCaptureDevice = undefined;
    this.audioBotEnabled = false;
  };

  /**
   * 验证权限
   */
  checkPermission(): Promise<{
    video: boolean;
    audio: boolean;
  }> {
    return VERTC.enableDevices({
      video: false,
      audio: true,
    });
  }

  /**
   * 获取设备
   */
  async getDevices(): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    const inputs = await VERTC.enumerateAudioCaptureDevices();
    const outputs = await VERTC.enumerateAudioPlaybackDevices();

    const audioInputs: MediaDeviceInfo[] = inputs.filter(
      (i) => i.deviceId && i.kind === 'audioinput',
    );
    const audioOutputs: MediaDeviceInfo[] = outputs.filter(
      (i) => i.deviceId && i.kind === 'audiooutput',
    );

    this._audioCaptureDevice = audioInputs.filter((i) => i.deviceId)?.[0]?.deviceId;

    return {
      audioInputs,
      audioOutputs,
    };
  }

  startAudioCapture = async (mic?: string) => {
    await this.engine.startAudioCapture(mic || this._audioCaptureDevice);
  };

  stopAudioCapture = async () => {
    await this.engine.stopAudioCapture();
  };

  publishStream = (mediaType: MediaType) => {
    this.engine.publishStream(mediaType);
  };

  unpublishStream = (mediaType: MediaType) => {
    this.engine.unpublishStream(mediaType);
  };

  setBusinessId = (businessId: string) => {
    this.engine.setBusinessId(businessId);
  };

  setAudioVolume = (volume: number) => {
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_MAIN, volume);
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_SCREEN, volume);
  };

  setAudioProfile = (profile: AudioProfileType) => {
    this.engine.setAudioProfile(profile);
  };

  switchDevice = (deviceType: 'microphone', deviceId: string) => {
    if (deviceType === 'microphone') {
      this._audioCaptureDevice = deviceId;
      this.engine.setAudioCaptureDevice(deviceId);
    }
  };

  getAudioBotEnabled = () => {
    return this.audioBotEnabled;
  };

  /**
   * 开始AIGC语音
   * @brief Enable AIGC
   */

  startAudioBot = async (
    roomId: string,
    userId: string,
  ) => {
    if (this.audioBotEnabled || sessionStorage.getItem('audioBotEnabled')) {
      await this.stopAudioBot(roomId, userId);
    }

    const config = aigcConfig.getAIGCConfig();

    let conversationId = localStorage.getItem("conversationId");
    /**
     * api-开启语音聊天
     */
    await openAPIs.StartVoiceChat({
      //你的音视频应用的唯一标志，参看获取 AppId
      AppId: aigcConfig.AppId,
      //房间的 ID，是房间的唯一标志。赋值规则参看参数赋值规范。
      RoomId: roomId,
      //智能体任务 ID。你必须对每个智能体任务设定 TaskId，且在后续进行任务更新和结束时也须使用该 TaskId。
      //AppId + RoomId + TaskId 是任务的唯一标识，可以用来标识指定 AppId 下某个房间内正在运行的任务，从而能在此任务运行中进行更新或者停止此任务。
      TaskId: userId,

      //ASR、TTS 和 LLM 相关高级配置。
      Config: {
        // 流式语音识别相关配置。
        ASRConfig: config.Config.ASRConfig,
        //语音合成相关配置。
        TTSConfig: config.Config.TTSConfig,
        //大模型相关配置，不同的大模型该参数结构不同：
        LLMConfig: {
          Mode: config.Config.LLMConfig.Mode,
          CozeBotConfig:{
            ...config.Config.LLMConfig.CozeBotConfig,
            UserId: userId,
            //用户参数
            Parameters: { "local_user_no": userId, "local_conv_id": conversationId},
          }
        },
        //打断模式。支持取值及含义如下：0：语音自动打断；1：禁用语音打断。
        InterruptMode: config.Config.InterruptMode,
      },

      //智能体基本配置。
      AgentConfig: {
        //房间内客户端 SDK 用户对应的 UserId。仅支持传入一个 UserId。
        TargetUserId: [userId],
        //智能体启动后的欢迎词。
        WelcomeMessage: config.AgentConfig.WelcomeMessage,
        //房间内智能体名称。赋值规则参看参数赋值规范。
        UserId: config.AgentConfig.UserId,
      },
    });
    this.audioBotEnabled = true;
    this.audioBotStartTime = Date.now();
    Utils.setSessionInfo({ audioBotEnabled: 'enable' });
  };

  /**
   * 停止AIGC语音
   * @brief Disable AIGC
   */
  stopAudioBot = async (roomId: string, userId: string) => {
    if (this.audioBotEnabled || sessionStorage.getItem('audioBotEnabled')) {
      await openAPIs.StopVoiceChat({
        AppId: aigcConfig.AppId,
        RoomId: roomId,
        TaskId: userId,
      });
      this.audioBotEnabled = false;
      this.audioBotStartTime = 0;
      sessionStorage.removeItem('audioBotEnabled');
    }
  };

  /**
   * 更新AIGC语音
   * @brief Command(Update) AIGC
   */
  commandAudioBot = async (roomId: string, userId: string, command: string) => {
    if (this.audioBotEnabled) {
      const res = await openAPIs.UpdateVoiceChat({
        AppId: aigcConfig.AppId,
        RoomId: roomId,
        TaskId: userId,
        Command: command,
      });
      return res;
    }
    return Promise.reject(new Error('AI Call failed'));
  };

  /**
   * 更新AIGC语音
   * @brief Update AIGC Configuration
   */
  updateAudioBot = async (
    roomId: string,
    userId: string,
  ) => {
    if (this.audioBotEnabled) {
      await this.stopAudioBot(roomId, userId);
      await this.startAudioBot(roomId, userId);
    } else {
      await this.startAudioBot(roomId, userId);
    }
  };
}

export default new RTCClient();
